import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, Loader2, Save, Award } from "lucide-react";
import {
  fetchCourseById,
  updateCourse,
  fetchCourses,
  clearCurrentCourse,
  finalizeCourse,
} from "../../redux/slices/courseSlice";
import { pickLocalized } from "../../lib/localized";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import CourseCertificateSettings from "./CourseCertificateSettings";
import CourseDaysPanel from "./CourseDaysPanel";
import CourseEnrollmentsPanel from "./CourseEnrollmentsPanel";
import CourseAnalyticsPanel from "./CourseAnalyticsPanel";
import ImageUploadField from "../../components/files/ImageUploadField";
import { MultilingualInput } from "../../components/common/MultilingualInput";
import {
  asMultilingual,
  hasEnglishText,
  pruneMultilingual,
} from "../../lib/multilingual-helpers";
import { useToast } from "../../components/ui/use-toast";
import {
  APP_TZ_LABEL,
  istDateTimeLocalToISO,
  toDateInputIST,
  toDateTimeLocalIST,
} from "../../lib/datetime-ist";

function CourseDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { currentCourse: course, loading } = useSelector(
    (state) => state.course,
  );

  const [title, setTitle] = useState({ en: "" });
  const [description, setDescription] = useState({ en: "" });
  const [durationDays, setDurationDays] = useState("7");
  const [taughtBy, setTaughtBy] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [courseStart, setCourseStart] = useState("");
  const [enrollmentStart, setEnrollmentStart] = useState("");
  const [enrollmentEnd, setEnrollmentEnd] = useState("");
  const [accessWindowStart, setAccessWindowStart] = useState("");
  const [accessWindowEnd, setAccessWindowEnd] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    if (id) dispatch(fetchCourseById(id));
    return () => {
      dispatch(clearCurrentCourse());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (!course) return;
    setTitle(asMultilingual(course.title));
    setDescription(asMultilingual(course.description));
    setDurationDays(String(course.duration_days ?? ""));
    setTaughtBy(course.taught_by || "");
    setThumbnailUrl(course.thumbnail_url || "");
    setCourseStart(toDateInputIST(course.course_start));
    setEnrollmentStart(toDateTimeLocalIST(course.enrollment_start));
    setEnrollmentEnd(toDateTimeLocalIST(course.enrollment_end));
    setAccessWindowStart(course.access_window_start || "");
    setAccessWindowEnd(course.access_window_end || "");
    setIsActive(!!course.is_active);
    setSettings(course.settings || {});
  }, [course]);

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!id || !course) return;
    const duration = parseInt(durationDays, 10);
    if (Number.isNaN(duration) || duration < 1) {
      toast({
        title: "Validation",
        description: "Duration must be at least 1 day",
        variant: "destructive",
      });
      return;
    }
    if (!hasEnglishText(title)) {
      toast({
        title: "Validation",
        description: "English title is required",
        variant: "destructive",
      });
      return;
    }
    const nextTitle = pruneMultilingual(title);
    const nextDesc = pruneMultilingual(description);

    setSaving(true);
    try {
      await dispatch(
        updateCourse({
          id,
          data: {
            title: nextTitle,
            description: nextDesc,
            duration_days: duration,
            is_active: isActive,
            taught_by: taughtBy.trim() || null,
            thumbnail_url: thumbnailUrl.trim() || null,
            start_date: courseStart || null,
            enrollment_start_date: enrollmentStart
              ? istDateTimeLocalToISO(enrollmentStart)
              : null,
            enrollment_end_date: enrollmentEnd
              ? istDateTimeLocalToISO(enrollmentEnd)
              : null,
            access_window_start: accessWindowStart || null,
            access_window_end: accessWindowEnd || null,
            certificate_template_id: course.certificate_template_id,
            settings,
          },
        }),
      ).unwrap();
      toast({ title: "Saved", description: "Course updated" });
      dispatch(fetchCourseById(id));
      dispatch(fetchCourses({}));
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Update failed",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (
      !window.confirm(
        "Finalize this course? This assigns final ranks and issues position certificates.",
      )
    ) {
      return;
    }
    setFinalizing(true);
    try {
      const result = await dispatch(finalizeCourse(id)).unwrap();
      toast({
        title: "Course finalized",
        description: `Position certificates: ${result?.position_certificates ?? 0}`,
      });
      dispatch(fetchCourseById(id));
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Finalize failed",
        variant: "destructive",
      });
    } finally {
      setFinalizing(false);
    }
  };

  if (loading && !course) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild>
          <Link to="/courses">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Link>
        </Button>
        <p className="text-muted-foreground">Course not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" asChild>
        <Link to="/courses">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Courses
        </Link>
      </Button>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">
            {pickLocalized(course.title) || "Course"}
          </h1>
          {course.taught_by && (
            <p className="text-muted-foreground">Taught by {course.taught_by}</p>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleFinalize}
          disabled={finalizing}
        >
          {finalizing ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Award className="w-4 h-4 mr-2" />
          )}
          Finalize course
        </Button>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
            <CardDescription>
              Course start date drives day windows and certificate finalization.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6 max-w-2xl">
            <MultilingualInput
              label="Title"
              value={title}
              onChange={setTitle}
              required
            />
            <MultilingualInput
              label="Description"
              value={description}
              onChange={setDescription}
              multiline
              rows={2}
            />
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="dur">Duration (days)</Label>
                <Input
                  id="dur"
                  type="number"
                  min={1}
                  value={durationDays}
                  onChange={(e) => setDurationDays(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="instructor">Instructor (certificates)</Label>
                <Input
                  id="instructor"
                  value={taughtBy}
                  onChange={(e) => setTaughtBy(e.target.value)}
                />
              </div>
            </div>
            <ImageUploadField
              label="Course thumbnail"
              hint="Shown on the courses list in the mobile app."
              value={thumbnailUrl}
              onChange={setThumbnailUrl}
              category="content_media"
            />
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="cstart">Course start (Day 1)</Label>
                <Input
                  id="cstart"
                  type="date"
                  value={courseStart}
                  onChange={(e) => setCourseStart(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ewin">
                  Daily quiz window ({APP_TZ_LABEL} HH:MM)
                </Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="09:00"
                    value={accessWindowStart}
                    onChange={(e) => setAccessWindowStart(e.target.value)}
                  />
                  <Input
                    placeholder="21:00"
                    value={accessWindowEnd}
                    onChange={(e) => setAccessWindowEnd(e.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Times are India Standard Time ({APP_TZ_LABEL}), e.g. 21:00 =
                  9:00 PM India.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="enrStart">
                  Enrollment opens ({APP_TZ_LABEL})
                </Label>
                <Input
                  id="enrStart"
                  type="datetime-local"
                  value={enrollmentStart}
                  onChange={(e) => setEnrollmentStart(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="enrEnd">
                  Enrollment closes ({APP_TZ_LABEL})
                </Label>
                <Input
                  id="enrEnd"
                  type="datetime-local"
                  value={enrollmentEnd}
                  onChange={(e) => setEnrollmentEnd(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="act"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
              <Label htmlFor="act">Active</Label>
            </div>
            <Button type="submit" disabled={saving} className="w-fit">
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save changes
            </Button>
          </CardContent>
        </Card>

        <CourseCertificateSettings
          settings={settings}
          onChange={setSettings}
        />
      </form>

      <CourseDaysPanel courseId={id} />
      <CourseAnalyticsPanel courseId={id} />
      <CourseEnrollmentsPanel courseId={id} />
    </div>
  );
}

export default CourseDetail;
